import { customerLabel } from '../lib/customerName';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INSIGHTS_AGENT_NAME } from '../lib/agentBranding';
import { screenLabel } from '../lib/contextSummary';
import {
  isOrgFilter,
  isVenuesFilter,
  outletIdFromFilter,
  restaurantIdsForScopeFilter,
  scopeFilterLabel,
  scopeGroupIdFromFilter,
  venueIdsFromFilter,
} from '../lib/scopeFilter';
import type { ContextPart, ScreenId, ViewContextState } from '../lib/viewContext';
import type {
  FleetRestaurant,
  PerfSettings,
  ReservationSourceId,
  ScopeGroupConfig,
  ScopeGroupSummary,
  StatusFilter,
} from '../types';
import {
  ContextBar,
  type DemoFocus,
  type FocusCrumb,
  type LinkNotice,
  type OwnWindow,
  type VenueChipInfo,
} from './context/ContextBar';
import { changedParts, ContextPanel, type ContextDraft } from './context/ContextPanel';
import { DisplaySettings } from './context/DisplaySettings';
import { useNarrow } from './context/overlay';
import { COMPARE_COLORS, MAX_COMPARE } from './context/VenueSection';
import { Icon } from './shared/Icon';
import '../styles/context-bar.css';

export { COMPARE_COLORS };

export type ComparisonItem = {
  filter: string;
  label: string;
  color: string;
  dataSource?: import('../types').ScopeGroupDataSource;
};

export const MAX_COMPARE_ITEMS = MAX_COMPARE;

/** Screens where venues can be compared side by side (scope comparison, `c=`). */
const COMPARE_MODES = new Set(['live', 'performance', 'settings']);

/**
 * The sticky top of every screen: the context bar (what the figures count,
 * Phase B step 3), Performance's and Live's display settings, and
 * maître d'AI. One Filters panel holds every control of the context and
 * applies them together.
 */
export function Topbar({
  mode,
  screen,
  perfSettings,
  onChangePerfSettings,
  scopeGroups,
  regionFilter,
  setRegionFilter,
  onOpenScopeSettings,
  outletCount,
  showScopeSettings,
  fleetRestaurants,
  groupName,
  accessibleRestaurants,
  onSwitchRestaurant,
  activeRestaurantId,
  venueIds = [],
  compareItems,
  onCompareChange,
  source,
  onChangeSource,
  status,
  onChangeStatus,
  shifts,
  onChangeShifts,
  availableShifts = [],
  onOpenAsk,
  agentBusy = false,
  restaurantId,
  scopeGroupConfigs = [],
  onScopeGroupsChanged,
  orgVenues = null,
  demoVenues = null,
  ownWindow = null,
  focus = [],
  link = null,
  screenToolsRef,
}: {
  mode: 'live' | 'performance' | 'settings' | 'growth' | 'horizon' | 'hotel' | 'assistant';
  /** The screen on display (lib/viewContext.ts): what it uses and how it counts. */
  screen: ScreenId;
  perfSettings: PerfSettings;
  onChangePerfSettings: (s: PerfSettings) => void;
  scopeGroups: ScopeGroupSummary[];
  regionFilter: string | null;
  setRegionFilter: (r: string | null) => void;
  onOpenScopeSettings: () => void;
  outletCount: number;
  showScopeSettings: boolean;
  fleetRestaurants?: FleetRestaurant[];
  /** The restaurant opened, by name. */
  groupName?: string;
  accessibleRestaurants?: FleetRestaurant[];
  onSwitchRestaurant?: (restaurantId: string) => void;
  activeRestaurantId?: string;
  /** The venues on screen, for the venue chip's list of names. */
  venueIds?: string[];
  compareItems: ComparisonItem[];
  onCompareChange: (items: ComparisonItem[]) => void;
  source: ReservationSourceId[];
  onChangeSource: (s: ReservationSourceId[]) => void;
  status: StatusFilter;
  onChangeStatus: (s: StatusFilter) => void;
  shifts: string[];
  onChangeShifts: (s: string[]) => void;
  availableShifts?: { name: string; outletCount: number }[];
  onOpenAsk: () => void;
  agentBusy?: boolean;
  /** Entry restaurant — the restaurant groups are saved under. */
  restaurantId: string;
  /** The person's own groups (with restaurantIds), kept on the server. */
  scopeGroupConfigs?: ScopeGroupConfig[];
  onScopeGroupsChanged?: () => void | Promise<void>;
  /** "All venues of <group>": the opened venue's group the person can open (decision 7.7.1). */
  orgVenues?: { name: string | null; restaurantIds: string[] } | null;
  /** Growth's demo group: its fictional venues, chosen inside Growth (the chip only names them). */
  demoVenues?: { label: string; names: string[]; focus?: DemoFocus | null } | null;
  /** The screen's own window (Forecast's outlook, Live's today). */
  ownWindow?: OwnWindow | null;
  /** Local drill-downs inside the context (Live's outlet, Forecast's "Showing", drills). */
  focus?: FocusCrumb[];
  /** Set while a shared link's context is on screen. */
  link?: LinkNotice | null;
  /**
   * A place in the tools for the screen's own controls (Growth's Display
   * menu and Notes button), filled by the screen through a portal.
   */
  screenToolsRef?: (el: HTMLDivElement | null) => void;
}) {
  const { t } = useTranslation('chrome');
  const topbarRef = useRef<HTMLElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const [compact, setCompact] = useState(false);
  const [panel, setPanel] = useState<ContextPart | null>(null);
  const narrow = useNarrow();

  useEffect(() => {
    const canvas = topbarRef.current?.closest('.ops-command')?.querySelector('.canvas');
    if (!(canvas instanceof HTMLElement)) return;
    const onScroll = () => setCompact(canvas.scrollTop > 10);
    onScroll();
    canvas.addEventListener('scroll', onScroll, { passive: true });
    return () => canvas.removeEventListener('scroll', onScroll);
  }, []);
  // Publish the measured height so full-screen drill-downs sit below the bar
  // (it wraps onto more rows when narrow).
  useLayoutEffect(() => {
    const el = topbarRef.current;
    const root = el?.closest('.ops-command');
    if (!(el instanceof HTMLElement) || !(root instanceof HTMLElement)) return;
    const sync = () => root.style.setProperty('--ops-topbar-height', `${el.offsetHeight}px`);
    sync();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty('--ops-topbar-height');
    };
  }, []);

  const state: ViewContextState = useMemo(
    () => ({ regionFilter, perfSettings, status, source, shifts }),
    [regionFilter, perfSettings, status, source, shifts],
  );
  const screenName = screenLabel(screen);
  const isComparing = compareItems.length >= 2;
  const compareSupported = COMPARE_MODES.has(mode) && showScopeSettings;

  const restaurantName = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of accessibleRestaurants ?? []) map.set(r.id, r.name);
    for (const r of fleetRestaurants ?? []) if (!map.has(r.id)) map.set(r.id, r.name);
    return (id: string) => map.get(id) ?? id;
  }, [accessibleRestaurants, fleetRestaurants]);
  const openedName = groupName?.trim() || t('scope.restaurant');
  const pickerRestaurants = accessibleRestaurants?.length ? accessibleRestaurants : (fleetRestaurants ?? []);

  const venues: VenueChipInfo = useMemo(() => {
    if (demoVenues) {
      return {
        label: demoVenues.label,
        count: demoVenues.names.length,
        names: demoVenues.names,
        origin: t('context.venues.origin.demo'),
        demo: true,
        demoFocus: demoVenues.focus ?? null,
      };
    }
    const ids = venueIds.length
      ? venueIds
      : restaurantIdsForScopeFilter(regionFilter, scopeGroupConfigs, activeRestaurantId, orgVenues?.restaurantIds ?? null);
    const names = ids.map(restaurantName);
    const groupId = scopeGroupIdFromFilter(regionFilter);
    const groupLabel = groupId ? scopeGroups.find(g => g.id === groupId)?.name ?? null : null;
    let label: string;
    let origin: string;
    if (link) {
      origin = t('context.venues.origin.link');
    } else if (isOrgFilter(regionFilter)) {
      const customer = customerLabel(orgVenues?.name);
      origin = customer
        ? t('context.venues.origin.org', { name: customer })
        : t('context.venues.origin.orgUnnamed');
    } else if (groupLabel) {
      origin = t('context.venues.origin.group', { name: groupLabel });
    } else if (!regionFilter || regionFilter === 'all') {
      origin = t('context.venues.origin.default');
    } else {
      origin = t('context.venues.origin.picker');
    }
    if (isVenuesFilter(regionFilter)) {
      const set = venueIdsFromFilter(regionFilter).map(restaurantName);
      label = set.length === 2 ? `${set[0]} · ${set[1]}` : set.length > 2 ? `${set[0]} +${set.length - 1}` : (set[0] ?? openedName);
    } else if (outletIdFromFilter(regionFilter)) {
      label = restaurantName(outletIdFromFilter(regionFilter)!);
    } else {
      label = scopeFilterLabel(regionFilter, scopeGroups, fleetRestaurants, openedName, orgVenues?.name ?? null);
    }
    // A set names its venues itself; a group or "All venues of" adds the count.
    const count = isVenuesFilter(regionFilter) && names.length <= 2 ? 1 : Math.max(outletCount, names.length);
    return { label, count, names, origin };
  }, [demoVenues, venueIds, regionFilter, scopeGroupConfigs, activeRestaurantId, orgVenues, restaurantName, scopeGroups, link, fleetRestaurants, openedName, outletCount, t]);

  const describeVenues = (filter: string) => {
    const ids = restaurantIdsForScopeFilter(filter, scopeGroupConfigs, activeRestaurantId, orgVenues?.restaurantIds ?? null);
    const label = isVenuesFilter(filter)
      ? (() => {
          const set = venueIdsFromFilter(filter).map(restaurantName);
          return set.length === 2 ? `${set[0]} · ${set[1]}` : set.length > 2 ? `${set[0]} +${set.length - 1}` : (set[0] ?? openedName);
        })()
      : compareLabel(filter);
    return ids.length > 1 && !(isVenuesFilter(filter) && ids.length <= 2)
      ? `${label} · ${t('context.venues.count', { count: ids.length })}`
      : label;
  };

  const compareLabel = (filter: string) => {
    const oid = outletIdFromFilter(filter);
    if (oid) return restaurantName(oid);
    return scopeFilterLabel(filter, scopeGroups, fleetRestaurants, openedName, orgVenues?.name ?? null);
  };

  const apply = (draft: ContextDraft) => {
    // Only the parts that changed are set, so a part a shared link set stays the link's.
    const changed = changedParts(draft, state, isComparing ? compareItems.map(i => i.filter) : []);
    // Venues compared side by side (Performance, Live, Settings), or one set of venues.
    const wasComparing = compareItems.map(i => i.filter);
    const comparingNext = draft.compare.supported && draft.compare.on && draft.compare.filters.length >= 2;
    if (comparingNext) {
      if (draft.compare.filters.join('|') !== wasComparing.join('|')) {
        onCompareChange(
          draft.compare.filters.map((filter, i) => ({
            filter,
            label: compareLabel(filter),
            color: COMPARE_COLORS[i % COMPARE_COLORS.length],
          })),
        );
      }
    } else if (wasComparing.length >= 2 && (!draft.compare.on || draft.compare.supported)) {
      onCompareChange([]);
    }
    if (!comparingNext && draft.regionFilter !== null && draft.regionFilter !== regionFilter) {
      applyVenues(draft.regionFilter);
    }
    if (changed.has('period') || changed.has('comparison')) onChangePerfSettings(draft.perfSettings);
    if (changed.has('statuses')) onChangeStatus(draft.status);
    if (changed.has('sources')) onChangeSource(draft.source);
    if (changed.has('shifts')) onChangeShifts(draft.shifts);
  };

  const applyVenues = (filter: string) => {
    setRegionFilter(filter);
    // Picking one other venue opens it; a set that leaves out the opened
    // restaurant opens its first venue, so Live and the entry restaurant stay
    // inside the venues on screen.
    const oid = outletIdFromFilter(filter);
    if (oid && oid !== activeRestaurantId) {
      onSwitchRestaurant?.(oid);
      return;
    }
    if (isVenuesFilter(filter) && activeRestaurantId) {
      const ids = venueIdsFromFilter(filter);
      if (ids.length && !ids.includes(activeRestaurantId)) onSwitchRestaurant?.(ids[0]!);
    }
  };

  const aiButton =
    mode !== 'assistant' ? (
      <button
        type="button"
        className={`tb-btn accent tb-ai${agentBusy ? ' tb-ai-busy' : ''}`}
        data-ftue="chrome-ai"
        onClick={() => {
          setPanel(null);
          onOpenAsk();
        }}
        aria-label={INSIGHTS_AGENT_NAME}
        title={agentBusy ? t('period.agentBusy', { name: INSIGHTS_AGENT_NAME }) : INSIGHTS_AGENT_NAME}
      >
        <Icon name="bolt" size={14} />
        <span className="tb-btn-text">{INSIGHTS_AGENT_NAME}</span>
        {agentBusy && <span className="tb-ai-busy-dot" aria-hidden />}
      </button>
    ) : null;

  const display =
    mode === 'performance' ? (
      <DisplaySettings settings={perfSettings} onChange={onChangePerfSettings} breakdown />
    ) : mode === 'live' ? (
      <DisplaySettings settings={perfSettings} onChange={onChangePerfSettings} breakdown={false} />
    ) : null;

  const screenTools = screenToolsRef ? <div className="cx-screen-tools" ref={screenToolsRef} /> : null;

  const tools =
    display || aiButton || screenTools ? (
      <div className="topbar-zone tb-tools cx-tools">
        {screenTools}
        {display}
        {aiButton}
      </div>
    ) : null;

  return (
    <header
      ref={topbarRef}
      className={`topbar cx-topbar${compact ? ' is-compact' : ''}${narrow ? ' is-narrow' : ''}`}
    >
      <div className="topbar-row topbar-row-main cx-topbar-row">
        <ContextBar
          screen={screen}
          state={state}
          venues={venues}
          comparing={isComparing ? { count: compareItems.length, supported: COMPARE_MODES.has(mode) } : null}
          ownWindow={ownWindow}
          focus={focus}
          link={link}
          narrow={narrow}
          onOpen={part => setPanel(part)}
          barRef={barRef}
          tools={narrow ? tools : undefined}
          onSetStatuses={status => {
            // The Filters panel's Apply for the statuses part (apply → onChangeStatus).
            setPanel(null);
            onChangeStatus(status);
          }}
        />
        {narrow ? null : tools}
      </div>

      {isComparing && COMPARE_MODES.has(mode) && (
        <div className="cmp-legend-bar" data-ftue="chrome-compare">
          {compareItems.map(item => (
            <span key={item.filter} className="cmp-chip">
              <span className="cmp-chip-dot" style={{ background: item.color }} />
              {item.label}
              <button
                type="button"
                className="cmp-chip-x"
                aria-label={t('scope.exitCompare')}
                onClick={() => onCompareChange(compareItems.filter(i => i.filter !== item.filter))}
              >
                <Icon name="close" size={11} />
              </button>
            </span>
          ))}
          <button
            type="button"
            className="cmp-exit-btn"
            onClick={() => {
              const lastFilter = compareItems[0]?.filter ?? 'all';
              onCompareChange([]);
              setRegionFilter(lastFilter);
            }}
          >
            {t('scope.exitCompare')}
          </button>
        </div>
      )}

      <ContextPanel
        open={panel !== null}
        onClose={() => setPanel(null)}
        anchorRef={barRef}
        sheet={narrow}
        initialSection={panel ?? 'venues'}
        screen={screen}
        screenName={screenName}
        state={state}
        compareNow={isComparing ? compareItems.map(i => i.filter) : []}
        onApply={apply}
        restaurants={pickerRestaurants}
        groups={scopeGroups}
        groupConfigs={scopeGroupConfigs}
        orgVenues={orgVenues}
        activeRestaurantId={activeRestaurantId ?? restaurantId}
        restaurantId={restaurantId}
        onGroupsChanged={onScopeGroupsChanged}
        onManageGroups={showScopeSettings ? onOpenScopeSettings : undefined}
        availableShifts={availableShifts}
        compareSupported={compareSupported}
        describeVenues={describeVenues}
      />
    </header>
  );
}
